(() => {
  const autoplaySelector = 'video[data-gcc-autoplay-video], video[autoplay]:not([controls]):not([data-video])';
  const managedVideos = new WeakSet();
  const hydratedVideos = new WeakSet();
  const visibleVideos = new Set();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const constrainedConnection = Boolean(connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || ''));

  function hydrate(video, priority = false) {
    if (!(video instanceof HTMLVideoElement) || hydratedVideos.has(video)) return;
    if (reducedMotion.matches || constrainedConnection) return;

    let changed = false;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
      changed = true;
    });

    if (video.dataset.gccSrc) {
      video.src = video.dataset.gccSrc;
      video.removeAttribute('data-gcc-src');
      changed = true;
    }

    video.preload = priority ? 'auto' : 'metadata';
    hydratedVideos.add(video);
    video.dataset.gccVideoHydrated = 'true';
    if (changed) video.load();
  }

  function play(video) {
    if (document.hidden || reducedMotion.matches) return;
    hydrate(video, video.hasAttribute('data-gcc-video-priority'));
    video.defaultMuted = true;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.play().catch(() => {});
  }

  const preloadObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        hydrate(entry.target, entry.target.hasAttribute('data-gcc-video-priority'));
        preloadObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '300px 0px' }
  );

  const playbackObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio > 0.05) {
          visibleVideos.add(video);
          play(video);
        } else {
          visibleVideos.delete(video);
          video.pause();
        }
      });
    },
    { threshold: [0, 0.05, 0.25] }
  );

  function manage(video) {
    if (!(video instanceof HTMLVideoElement) || managedVideos.has(video)) return;
    managedVideos.add(video);
    video.defaultMuted = true;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    video.addEventListener('waiting', () => video.classList.add('gcc-video-is-buffering'));
    video.addEventListener('playing', () => video.classList.remove('gcc-video-is-buffering'));
    video.addEventListener('canplay', () => {
      if (visibleVideos.has(video)) play(video);
    });
    preloadObserver.observe(video);
    playbackObserver.observe(video);
    if (video.hasAttribute('data-gcc-video-priority')) hydrate(video, true);
  }

  function scan(root = document) {
    if (root.matches?.(autoplaySelector)) manage(root);
    root.querySelectorAll?.(autoplaySelector).forEach(manage);
  }

  function applyMotionPreference() {
    if (reducedMotion.matches) {
      visibleVideos.forEach((video) => video.pause());
    } else {
      visibleVideos.forEach(play);
    }
  }

  function init() {
    scan();
    new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node instanceof Element) scan(node);
      }));
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        visibleVideos.forEach((video) => video.pause());
      } else {
        visibleVideos.forEach(play);
      }
    });
    reducedMotion.addEventListener?.('change', applyMotionPreference);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
